import { useCallback, useEffect } from 'react'
import { useTranslations } from '@npco/common-utils-translations'
import {
  AnnouncementModal,
  AnnouncementModalBodyContent,
  AnnouncementModalBodyDescription,
  Box,
  Carousel,
  COLOR,
  Flex,
  Heading,
  NewBadge,
  useModalState,
} from '@npco/zeller-design-system'

import { useIsMobileResolution } from 'hooks/useIsMobileResolution'
import { zIndexMap } from 'const/zIndex'
import { translationsShared } from 'translations'

import { useInvoicingWelcomeModal } from './hooks/useInvoicingWelcomeModal'

export const InvoicingWelcomeModal = () => {
  const isMobile = useIsMobileResolution()
  const tShared = useTranslations(translationsShared)

  const {
    isSingleSlide,
    markSlideAsSeen,
    setSlideIndex,
    shouldShowInvoicingWelcomeModal,
    slideIndex,
    slides,
  } = useInvoicingWelcomeModal()

  const { closeModal, isModalOpen } = useModalState(
    shouldShowInvoicingWelcomeModal
  )

  const isOnFirstIndex = slideIndex === 0
  const isOnLastIndex = slideIndex === slides.length - 1
  const firstSlide = slides[0]

  const handleClickPrimary = useCallback(() => {
    if (isOnLastIndex) {
      closeModal()
      return
    }

    setSlideIndex((index) => index + 1)
  }, [closeModal, isOnLastIndex, setSlideIndex])

  const handleClickSecondary = useCallback(() => {
    if (isOnFirstIndex) {
      closeModal()
      return
    }

    setSlideIndex((index) => index - 1)
  }, [closeModal, isOnFirstIndex, setSlideIndex])

  const handleSetSlideIndex = useCallback(
    (index?: number) => {
      // NOTE: when a slide is clicked the handler here is returned NaN so we
      // must handle this appropriately
      if (index) {
        setSlideIndex(index)
      }
    },
    [setSlideIndex]
  )

  useEffect(() => {
    if (!shouldShowInvoicingWelcomeModal) {
      return
    }

    const { key } = slides[slideIndex]

    markSlideAsSeen(key)
  }, [markSlideAsSeen, shouldShowInvoicingWelcomeModal, slides, slideIndex])

  if (!shouldShowInvoicingWelcomeModal) {
    return null
  }

  const svgHeight = isMobile ? 140 : 180
  const svgWidth = isMobile ? 140 : 180

  if (isSingleSlide) {
    const { Svg, description, heading, key } = firstSlide

    return (
      <AnnouncementModal
        isOpen={isModalOpen}
        onCancel={closeModal}
        onClickPrimary={handleClickPrimary}
        primaryButtonLabel={tShared('done')}
      >
        <Box
          left="0px"
          position="absolute"
          top="16px"
          zIndex={zIndexMap.announcement}
        >
          <NewBadge text={tShared('new')} />
        </Box>
        <AnnouncementModalBodyContent key={key}>
          <Flex alignSelf="center">
            <Svg height={svgHeight} width={svgWidth} />
          </Flex>
          <Heading.H2>{heading}</Heading.H2>
          <AnnouncementModalBodyDescription>
            {description}
          </AnnouncementModalBodyDescription>
        </AnnouncementModalBodyContent>
      </AnnouncementModal>
    )
  }

  return (
    <AnnouncementModal
      isOpen={isModalOpen}
      onCancel={closeModal}
      onClickPrimary={handleClickPrimary}
      onClickSecondary={handleClickSecondary}
      primaryButtonLabel={isOnLastIndex ? tShared('finish') : tShared('next')}
      secondaryButtonLabel={isOnFirstIndex ? tShared('close') : tShared('back')}
    >
      <Box
        left="0px"
        position="absolute"
        top="16px"
        zIndex={zIndexMap.announcement}
      >
        <NewBadge text={tShared('new')} />
      </Box>
      <Carousel
        bgColor={COLOR.WHITE}
        setSlideIndex={handleSetSlideIndex}
        slideIndex={slideIndex}
      >
        {slides.map((slide) => (
          <AnnouncementModalBodyContent key={slide.key}>
            <Flex alignSelf="center">
              <slide.Svg height={svgHeight} width={svgWidth} />
            </Flex>
            <Heading.H2>{slide.heading}</Heading.H2>
            <AnnouncementModalBodyDescription>
              {slide.description}
            </AnnouncementModalBodyDescription>
          </AnnouncementModalBodyContent>
        ))}
      </Carousel>
    </AnnouncementModal>
  )
}
